import React, { useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import GlobalStateContext from "@/GlobalStateContext";
import { useLocation } from "react-router-dom";
import type { ContentItemRecord } from "@/types/contentItemRecord";
import type { ImageProps } from "@/types/imageProps";

export interface GlobalState {
    Bedrijfsnaam: string;
    Language: string;
    ContentLanguage?: string;
    Contentitems: ContentItemRecord[];
    Globalcontent: ContentItemRecord[];
    LoadedContentFiles: string[];
    Location: {
        pathname: string;
        search: string;
        hash: string;
        key?: string;
    };
    Logo: ImageProps;
}

export interface GlobalStateContextType {
    globalState: GlobalState;
    setGlobalState: React.Dispatch<React.SetStateAction<GlobalState>>;
    updateGlobalState: (patch: Partial<GlobalState>) => void;
}

const defaultLocale =
    typeof localStorage !== "undefined" && localStorage.getItem("locale")
        ? (localStorage.getItem("locale") as string)
        : "nl-NL";

const GlobalStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [globalState, setGlobalState] = useState<GlobalState>({
        Bedrijfsnaam: "",
        Language: defaultLocale,
        Contentitems: [],
        Globalcontent: [],
        LoadedContentFiles: [],
        Location: { pathname: "/", search: "", hash: "" },
        Logo: { url: "", alt: "", title: "" }
    });

    const location = useLocation();

    // Immutabele merge-helper
    const updateGlobalState = useCallback((patch: Partial<GlobalState>) => {
        setGlobalState(prev => ({ ...prev, ...patch }));
    }, []);

    useEffect(() => {
        // alleen bij relevante wijzigingen updaten
        setGlobalState(s => ({
            ...s,
            Location: {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                key: (location as any).key, // key is niet in alle routers getypt
            },
        }));
    }, [location.pathname, location.search, location.hash, (location as any).key]);

    // 👇 voorkom onnodige re-renders van consumers
    const value = useMemo(
        () => ({ globalState, setGlobalState, updateGlobalState }),
        [globalState, updateGlobalState]
    );

    return (
        <GlobalStateContext.Provider value={value}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export default GlobalStateProvider;
